import React, { useState, DragEvent } from "react";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    Pagination,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { globalMailSchema } from "../../Interfaces/IGlobalMail";
import { IGlobalMail } from "../../Interfaces/IGlobalMail";
import { CustomEditor } from "../CustomEditor";
import { useCreateGlobalMail, useGetGlobalMail, GLOBAL_MAIL_PAGE_SIZE } from "../../hooks/mailing";
import { useAlert } from "../../core/AlertContext";
import { useGetGroups, useGetUsers } from "../../hooks/users";
import { ErrorTryRefetch } from "../../core/ErrorTryRefetch";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import PageHeader from "../../core/PageHeader";
import {
    cardLgSx,
    fieldSx,
    primaryButtonSx,
    secondaryButtonSx,
    makeIconCircleSx,
    GREEN_700,
    GOLD_MUTED,
} from "../../core/pvtaStyles";

// Einheitlicher Abschnittskopf: goldener Icon-Kreis + Titel + dezente Hilfsbeschreibung.
const FormSection: React.FC<{
    icon: React.ReactNode;
    title: string;
    description?: string;
    required?: boolean;
    children: React.ReactNode;
}> = ({ icon, title, description, required, children }) => (
    <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box sx={makeIconCircleSx(38)}>{icon}</Box>
            <Box sx={{ minWidth: 0, pt: 0.25 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: 16, md: 17 },
                        color: 'var(--pvta-heading)',
                        lineHeight: 1.25,
                    }}
                >
                    {title}
                    {required && (
                        <Box component="span" sx={{ color: GOLD_MUTED, ml: 0.5 }}>*</Box>
                    )}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', display: 'block', mt: 0.25 }}>
                        {description}
                    </Typography>
                )}
            </Box>
        </Stack>
        <Box sx={{ mt: 2, minWidth: 0 }}>{children}</Box>
    </Box>
);

export const GlobalMail: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [editGlobalMail, setEditGlobalMail] = useState(globalMailSchema.parse({}));
    const [attachments, setAttachments] = useState<File[]>([]);
    const [baseBundesbruderIds, setBaseBundesbruderIds] = useState<number[]>([]);
    const [manualIds, setManualIds] = useState<number[]>([]);
    const [selectedMail, setSelectedMail] = useState<IGlobalMail | null>(null);
    const [showSentMails, setShowSentMails] = useState(false);
    const [sentPage, setSentPage] = useState(1);
    const { createGlobalMail, isPendingGlobalMail } = useCreateGlobalMail();
    const { globalMails, globalMailCount, isLoadingGlobalMailArray, refetchGlobalMailArray, globalMailLoaded } = useGetGlobalMail(sentPage);
    const { showAlert } = useAlert();

    const { userArray, isLoadingUserArray, refetchUserArray } = useGetUsers();
    const { groupArray, isLoadingGroupArray, refetechGroupArray } = useGetGroups();

    const handleFiles = (files: FileList) => {
        const fileArray = Array.from(files).filter((f) =>
            [".pdf", ".png", ".jpg", ".jpeg"].some((ext) =>
                f.name.toLowerCase().endsWith(ext)
            )
        );
        setAttachments((prev) => [...prev, ...fileArray]);
    };

    const handleDrop = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    const handleDragOver = (e: DragEvent<HTMLElement>) => {
        e.preventDefault();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    const getFinalRecipientIds = (): number[] => {
        return Array.from(new Set([...baseBundesbruderIds, ...manualIds]));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("subject", editGlobalMail.subject);
        formData.append("body", editGlobalMail.body);
        editGlobalMail.to_groups.forEach((g) => formData.append("to_groups", String(g)));
        getFinalRecipientIds().forEach((u) => formData.append("to_users", String(u)));
        attachments.forEach((file) => formData.append("attachments", file));

        try {
            await createGlobalMail(formData);
            showAlert({ text: 'Rundmail erfolgreich versendet.' });
            setEditGlobalMail(globalMailSchema.parse({}));
            setAttachments([]);
            setManualIds([]);
            setBaseBundesbruderIds([]);
            setSentPage(1);
            void refetchGlobalMailArray();
        } catch (error) {
            showAlert({ text: 'Fehler beim Speichern der Rundmail.', severity: 'error' });
            console.error(error);
        }
    };

    if (isLoadingUserArray || isLoadingGroupArray || isLoadingGlobalMailArray) {
        return (
            <Box sx={{ py: { xs: 3, md: 5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
                    <Skeleton variant="text" width={260} height={48} sx={{ mb: 1, maxWidth: '100%' }} />
                    <Skeleton variant="text" width={360} height={24} sx={{ mb: 3, maxWidth: '100%' }} />
                    <Card sx={{ ...cardLgSx, p: { xs: 2.5, sm: 4 } }}>
                        <Stack spacing={3} divider={<Divider flexItem />}>
                            {[260, 220, 56, 240, 120].map((height, index) => (
                                <Box key={index}>
                                    <Skeleton variant="text" width="40%" />
                                    <Skeleton variant="rounded" height={height} sx={{ mt: 1.5, borderRadius: 'var(--pvta-radius-sm)' }} />
                                </Box>
                            ))}
                        </Stack>
                    </Card>
                </Container>
            </Box>
        );
    }
    if (!userArray || !groupArray || !globalMailLoaded) {
        return (
            <ErrorTryRefetch refetchFunction={() => {
                refetchUserArray();
                refetechGroupArray();
                void refetchGlobalMailArray();
            }} />
        );
    }

    const combinedIds = Array.from(new Set([...baseBundesbruderIds, ...manualIds]));
    const combinedUsers = userArray.filter((u) => combinedIds.includes(u.id));

    const handleManualDelete = (id: number) => {
        setManualIds((prev) => prev.filter((uid) => uid !== id));
    };

    const getRecipientNames = (mail: IGlobalMail): string[] => {
        return userArray
            .filter((user) => mail.to_users.includes(user.id))
            .map((user) => `${user.first_name} ${user.last_name}`.trim() || user.email);
    };

    const getGroupNames = (mail: IGlobalMail): string[] => {
        if (mail.to_group_names.length > 0) {
            return mail.to_group_names;
        }
        return groupArray
            .filter((group) => mail.to_groups.includes(group.id))
            .map((group) => group.customGroup);
    };

    return (
        <Box sx={{ py: { xs: 3, md: 5 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader
                    title="Rundmail versenden"
                    subtitle="Erreichen Sie gezielt Ihre Bundesbrüder mit einer persönlichen Nachricht."
                />

                <Card
                    sx={{
                        ...cardLgSx,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        p: { xs: 2.5, sm: 4 },
                    }}
                >
                    <Box component="form" onSubmit={handleSubmit} sx={{ minWidth: 0 }}>
                        <Stack spacing={{ xs: 3, md: 4 }} divider={<Divider flexItem />} sx={{ minWidth: 0 }}>
                            {/* A) Empfängergruppen */}
                            <FormSection
                                icon={<GroupsOutlinedIcon fontSize="small" />}
                                title="Empfängergruppen"
                                description="Wähle eine oder mehrere Gruppen aus – oder adressiere alle Bundesbrüder auf einmal."
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                    sx={{ minWidth: 0 }}
                                >
                                    <Autocomplete
                                        multiple
                                        options={groupArray}
                                        getOptionLabel={(opt) => opt.customGroup}
                                        value={groupArray.filter((g) => editGlobalMail.to_groups.includes(g.id))}
                                        onChange={(_, newVal) =>
                                            setEditGlobalMail((prev) => ({ ...prev, to_groups: newVal.map((x) => x.id) }))
                                        }
                                        renderInput={(params) => (
                                            <TextField {...params} placeholder="Gruppen auswählen" />
                                        )}
                                        sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                                    />
                                    <Button
                                        onClick={() => {
                                            const ids = userArray
                                                .filter((u) => !u.verstorben && !u.customGroups.includes(4))
                                                .map((u) => u.id);
                                            setBaseBundesbruderIds(ids);
                                        }}
                                        startIcon={<GroupsOutlinedIcon />}
                                        sx={{
                                            ...secondaryButtonSx,
                                            width: { xs: '100%', sm: 'auto' },
                                            height: { xs: 48, sm: 56 },
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Alle Bundesbrüder
                                    </Button>
                                </Stack>
                            </FormSection>

                            {/* B) Einzelne Empfänger */}
                            <FormSection
                                icon={<PersonOutlineIcon fontSize="small" />}
                                title="Einzelne Empfänger"
                                description="Ergänze einzelne Personen zusätzlich zu den gewählten Gruppen."
                            >
                                <Autocomplete
                                    multiple
                                    options={userArray.filter((u) => u.verstorben === null)}
                                    getOptionLabel={(opt) => `${opt.first_name} ${opt.last_name}`}
                                    value={combinedUsers}
                                    onChange={(_, newVal) => {
                                        const newIds = newVal.map((u) => u.id);
                                        const neueManuelle = newIds.filter((id) => !baseBundesbruderIds.includes(id));
                                        const neueBase = baseBundesbruderIds.filter((id) => newIds.includes(id));
                                        setManualIds(neueManuelle);
                                        setBaseBundesbruderIds(neueBase);
                                    }}
                                    renderTags={() => {
                                        const tags: React.ReactNode[] = [];
                                        if (baseBundesbruderIds.length > 0) {
                                            tags.push(
                                                <Chip
                                                    key="bundesbrueder-tag"
                                                    label={`Alle Bundesbrüder (${baseBundesbruderIds.length})`}
                                                    onDelete={() => setBaseBundesbruderIds([])}
                                                />
                                            );
                                        }
                                        manualIds.forEach((id) => {
                                            const user = userArray.find((u) => u.id === id);
                                            if (user) {
                                                tags.push(
                                                    <Chip
                                                        key={`manual-${id}`}
                                                        label={`${user.first_name} ${user.last_name}`}
                                                        onDelete={() => handleManualDelete(id)}
                                                    />
                                                );
                                            }
                                        });
                                        return tags;
                                    }}
                                    renderInput={(params: AutocompleteRenderInputParams) => (
                                        <TextField
                                            {...params}
                                            placeholder="Empfänger auswählen"
                                            fullWidth
                                        />
                                    )}
                                    sx={{ ...fieldSx, minWidth: 0 }}
                                />
                                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--pvta-muted)' }}>
                                    Empfänger: {combinedIds.length} Personen (davon {baseBundesbruderIds.length} Bundesbrüder)
                                </Typography>
                            </FormSection>

                            {/* C) Betreff */}
                            <FormSection
                                icon={<SubjectOutlinedIcon fontSize="small" />}
                                title="Betreff"
                                required
                                description="Eine prägnante Betreffzeile erhöht die Lesequote."
                            >
                                <TextField
                                    variant="outlined"
                                    placeholder="Betreff der Rundmail"
                                    value={editGlobalMail.subject}
                                    onChange={(e) =>
                                        setEditGlobalMail((prev) => ({ ...prev, subject: e.target.value }))
                                    }
                                    required
                                    fullWidth
                                    error={!editGlobalMail.subject}
                                    sx={{ ...fieldSx, minWidth: 0 }}
                                />
                            </FormSection>

                            {/* D) Nachricht */}
                            <FormSection
                                icon={<NotesOutlinedIcon fontSize="small" />}
                                title="Nachricht"
                                required
                                description="Verfasse den Inhalt deiner Rundmail. Über „HTML Ansicht“ kannst du den Quelltext bearbeiten."
                            >
                                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                                    <CustomEditor
                                        txt={editGlobalMail.body}
                                        setTxt={(body: string) =>
                                            setEditGlobalMail((prev) => ({ ...prev, body }))
                                        }
                                        placeholder="Ihre Nachricht an die Empfänger …"
                                    />
                                </Box>
                            </FormSection>

                            {/* E) Dateien anhängen */}
                            <FormSection
                                icon={<AttachFileOutlinedIcon fontSize="small" />}
                                title="Dateien anhängen"
                                description="Optional – unterstützt werden PDF-, PNG- und JPG-Dateien."
                            >
                                <Box
                                    component="label"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    sx={{
                                        display: 'block',
                                        p: { xs: 3, sm: 4 },
                                        border: '1.5px dashed',
                                        borderColor: 'var(--pvta-border-strong)',
                                        borderRadius: 'var(--pvta-radius-md)',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        backgroundColor: 'var(--pvta-bg-soft)',
                                        transition: 'border-color 200ms ease, background-color 200ms ease',
                                        '&:hover': {
                                            borderColor: GREEN_700,
                                            backgroundColor: 'var(--pvta-green-100)',
                                        },
                                    }}
                                >
                                    <input
                                        type="file"
                                        hidden
                                        multiple
                                        accept=".pdf,.png,.jpg,.jpeg"
                                        onChange={handleFileChange}
                                    />
                                    <Stack alignItems="center" spacing={1}>
                                        <Box sx={makeIconCircleSx(48)}>
                                            <CloudUploadOutlinedIcon />
                                        </Box>
                                        <Typography sx={{ fontWeight: 700, color: 'var(--pvta-heading)' }}>
                                            Dateien hierher ziehen und ablegen
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: GREEN_700, fontWeight: 600 }}>
                                            oder Dateien auswählen
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>
                                            Unterstützte Formate: PDF, PNG, JPG
                                        </Typography>
                                    </Stack>
                                </Box>
                                {attachments.length > 0 && (
                                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {attachments.map((file, idx) => (
                                            <Chip
                                                key={idx}
                                                label={file.name}
                                                onDelete={() =>
                                                    setAttachments((prev) => prev.filter((_, i) => i !== idx))
                                                }
                                                sx={{ maxWidth: '100%' }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </FormSection>

                            {/* F) Primäre Aktion */}
                            <Box sx={{ minWidth: 0 }}>
                                {isPendingGlobalMail && (
                                    <LinearProgress sx={{ borderRadius: 999, mb: 2 }} />
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        type="submit"
                                        disabled={isPendingGlobalMail}
                                        startIcon={
                                            isPendingGlobalMail
                                                ? <CircularProgress size={18} sx={{ color: 'inherit' }} />
                                                : <SendRoundedIcon />
                                        }
                                        sx={{
                                            ...primaryButtonSx,
                                            width: { xs: '100%', sm: 'auto' },
                                            minHeight: 48,
                                            px: 4,
                                        }}
                                    >
                                        Senden
                                    </Button>
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                </Card>

                {/* Bereits versendete Rundmails */}
                <Card
                    sx={{
                        ...cardLgSx,
                        mt: 3,
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        p: { xs: 2, sm: 3 },
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        onClick={() => setShowSentMails((prev) => !prev)}
                        role="button"
                        aria-expanded={showSentMails}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            minWidth: 0,
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                            <Box sx={makeIconCircleSx(38)}>
                                <MarkEmailReadOutlinedIcon fontSize="small" />
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, color: 'var(--pvta-heading)', minWidth: 0 }} noWrap>
                                Bereits versendete Rundmails ({globalMailCount})
                            </Typography>
                        </Stack>
                        <IconButton size="small" aria-label={showSentMails ? 'Einklappen' : 'Ausklappen'} sx={{ flexShrink: 0 }}>
                            {showSentMails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    <Collapse in={showSentMails} timeout="auto" unmountOnExit>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                            Hier siehst du deine bereits versendeten Rundmails. Als Superuser werden alle versendeten Rundmails angezeigt.
                        </Typography>
                        {globalMailCount === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                Bisher wurden keine Rundmails gefunden.
                            </Typography>
                        ) : (
                            <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                                {globalMails.map((mail) => (
                                    <Card
                                        key={mail.id}
                                        variant="outlined"
                                        sx={{
                                            p: { xs: 1.75, sm: 2 },
                                            borderRadius: 'var(--pvta-radius-md)',
                                            borderColor: 'var(--pvta-border-strong)',
                                            minWidth: 0,
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={1}
                                            justifyContent="space-between"
                                            sx={{ minWidth: 0 }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
                                                {mail.subject}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                                {mail.date_send ? new Date(mail.date_send).toLocaleString('de-DE') : ''}
                                            </Typography>
                                        </Stack>
                                        <Stack spacing={0.75} sx={{ mt: 1, minWidth: 0 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Absender: {mail.from_user_name || 'Unbekannt'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Einzelne Empfänger: {mail.to_user_count}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Gruppen: {mail.to_group_names.length > 0 ? mail.to_group_names.join(', ') : 'Keine'}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.primary',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(mail.body) }}
                                            />
                                            <Box sx={{ pt: 0.5 }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => setSelectedMail(mail)}
                                                    sx={{ ...secondaryButtonSx, minHeight: 38 }}
                                                >
                                                    Details ansehen
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                        {globalMailCount > GLOBAL_MAIL_PAGE_SIZE && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                <Pagination
                                    count={Math.ceil(globalMailCount / GLOBAL_MAIL_PAGE_SIZE)}
                                    page={sentPage}
                                    onChange={(_, value) => setSentPage(value)}
                                    color="primary"
                                    siblingCount={0}
                                />
                            </Stack>
                        )}
                    </Collapse>
                </Card>
            </Container>

            <Dialog
                open={selectedMail !== null}
                onClose={() => setSelectedMail(null)}
                fullWidth
                maxWidth="md"
                fullScreen={isMobile}
            >
                {selectedMail && (
                    <>
                        <DialogTitle
                            sx={{
                                px: { xs: 1.25, sm: 2.5 },
                                py: { xs: 1, sm: 1.5 },
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Box sx={{ minWidth: 0, pr: 1 }}>
                                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700 }}>
                                        {selectedMail.subject}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedMail.date_send ? new Date(selectedMail.date_send).toLocaleString('de-DE') : ''}
                                    </Typography>
                                </Box>
                                <IconButton onClick={() => setSelectedMail(null)} size="small">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </DialogTitle>
                        <DialogContent
                            dividers
                            sx={{
                                px: { xs: 1, sm: 2.5 },
                                py: { xs: 1, sm: 2 },
                            }}
                        >
                            <Stack spacing={2}>
                                <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'var(--pvta-radius-md)' }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Versanddetails
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Absender: {selectedMail.from_user_name || 'Unbekannt'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Einzelne Empfänger: {selectedMail.to_user_count}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Empfängergruppen: {getGroupNames(selectedMail).length > 0 ? getGroupNames(selectedMail).join(', ') : 'Keine'}
                                        </Typography>
                                    </Stack>
                                </Card>
                                <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'var(--pvta-radius-md)' }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Einzelne Empfänger
                                        </Typography>
                                        {getRecipientNames(selectedMail).length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {getRecipientNames(selectedMail).map((name, index) => (
                                                    <Chip key={`${name}-${index}`} label={name} size="small" />
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Keine einzelnen Empfänger hinterlegt.
                                            </Typography>
                                        )}
                                    </Stack>
                                </Card>
                                <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'var(--pvta-radius-md)' }}>
                                    <Stack spacing={1}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            Nachricht
                                        </Typography>
                                        <Box
                                            sx={{
                                                color: 'text.primary',
                                                '& p': { mt: 0, mb: 1.5 },
                                                '& p:last-child': { mb: 0 },
                                                '& ul, & ol': { pl: 3, my: 1 },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedMail.body) }}
                                        />
                                    </Stack>
                                </Card>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1, sm: 1.5 } }}>
                            <Button onClick={() => setSelectedMail(null)}>Schließen</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};
